
import React, { useState, useEffect, useCallback } from 'react';
import TransactionList from '@/components/TransactionList';
import { Card, CardContent } from '@/components/ui/card';
import TransactionFilterBar from '@/components/dashboard/TransactionFilterBar';
import { getStatusFilterForTab, getTabValueFromStatus } from '@/utils/transactionStatusMappings';
import { OnboardingBlocker } from '@/components/onboarding';

interface TransactionsTabProps {
  filterStatus?: string;
  searchQuery?: string;
  showFilterBar?: boolean;
  stripeConnected: boolean;
}

const TransactionsTab: React.FC<TransactionsTabProps> = ({ 
  filterStatus = 'all',
  searchQuery = '',
  showFilterBar = true,
  stripeConnected
}) => {
  const [activeTab, setActiveTab] = useState('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filters, setFilters] = useState<Record<string, string | null>>({
    paymentMethod: null,
    keepMoreStatus: null,
    minAmount: null
  });

  // Dispatch a custom event to trigger refresh in TransactionList
  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    const refreshEvent = new CustomEvent("transaction-force-refresh");
    document.dispatchEvent(refreshEvent);
    // Reset refreshing state after a short delay
    setTimeout(() => setIsRefreshing(false), 2000);
  }, []);
  
  const handleTabChange = (value: string) => {
    console.log('[TransactionsTab] Tab changed to:', value);
    setActiveTab(value);
  };
  
  const handleFilterChange = (filterType: string, value: string | null) => {
    console.log(`[TransactionsTab] Filter changed: ${filterType} = ${value}`);
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };
  
  const clearAllFilters = () => {
    console.log('[TransactionsTab] Clearing all filters');
    setFilters({
      paymentMethod: null,
      keepMoreStatus: null,
      minAmount: null
    });
  };
  
  useEffect(() => {
    if (filterStatus) {
      const tabValue = getTabValueFromStatus(filterStatus);
      setActiveTab(tabValue);
    }
  }, [filterStatus]);

  return (
    <OnboardingBlocker requireStripe={true} customMessage="Connect your Stripe account to view transaction data and payment history.">
      <div className="space-y-4">
        <Card className="border shadow-sm">
          {showFilterBar && (
            <div className="px-4 py-4 border-b border-border/20">
              <TransactionFilterBar
                activeTab={activeTab}
                onTabChange={handleTabChange}
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={clearAllFilters}
                onRefresh={handleRefresh}
                isRefreshing={isRefreshing}
              />
            </div>
          )}
          <CardContent className="p-0">
            <TransactionList 
              filterStatus={getStatusFilterForTab(activeTab)} 
              stripeConnected={stripeConnected}
              paymentMethodFilter={filters.paymentMethod}
              keepMoreFilter={filters.keepMoreStatus}
              minAmountFilter={filters.minAmount ? parseFloat(filters.minAmount) : undefined}
              searchQuery={searchQuery}
            />
          </CardContent>
        </Card>
      </div>
    </OnboardingBlocker>
  );
};

export default TransactionsTab;
